/**
 * CollisionWorld の AABB / OBB / 三角柱をワイヤーフレームで描く。
 * 通常の見た目メッシュではなく、物理が実際に使うコライダだけを出す。
 */

import * as THREE from 'three';
import { prismVertices, type Obb, type TrianglePrism } from '../shared/obb';
import type { Aabb, Collider, CollisionWorld } from '../shared/physics';
import type { Vec3 } from '../shared/types';

const WALKABLE_COLOR = 0x3ddc6a;
const SOLID_COLOR = 0xff22cc;
const CLEAR_COLOR = 0x111111;

const PRISM_EDGE_PAIRS = [
  [0, 1],
  [1, 2],
  [2, 0],
  [3, 4],
  [4, 5],
  [5, 3],
  [0, 3],
  [1, 4],
  [2, 5],
] as const;

const EDGE_PAIRS = [
  [0, 1],
  [2, 3],
  [4, 5],
  [6, 7],
  [0, 2],
  [1, 3],
  [4, 6],
  [5, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
] as const;

function aabbCorners(box: Aabb, out: Vec3[]): void {
  const { min, max } = box;
  out[0] = { x: min.x, y: min.y, z: min.z };
  out[1] = { x: max.x, y: min.y, z: min.z };
  out[2] = { x: min.x, y: max.y, z: min.z };
  out[3] = { x: max.x, y: max.y, z: min.z };
  out[4] = { x: min.x, y: min.y, z: max.z };
  out[5] = { x: max.x, y: min.y, z: max.z };
  out[6] = { x: min.x, y: max.y, z: max.z };
  out[7] = { x: max.x, y: max.y, z: max.z };
}

function obbCorners(obb: Obb, out: Vec3[]): void {
  const { center, half, axes } = obb;
  let index = 0;
  for (const sz of [-1, 1] as const) {
    for (const sy of [-1, 1] as const) {
      for (const sx of [-1, 1] as const) {
        out[index++] = {
          x: center.x + sx * half.x * axes[0].x + sy * half.y * axes[1].x + sz * half.z * axes[2].x,
          y: center.y + sx * half.x * axes[0].y + sy * half.y * axes[1].y + sz * half.z * axes[2].y,
          z: center.z + sx * half.x * axes[0].z + sy * half.y * axes[1].z + sz * half.z * axes[2].z,
        };
      }
    }
  }
}

function pushBoxEdges(corners: Vec3[], positions: number[]): void {
  for (const [a, b] of EDGE_PAIRS) {
    const from = corners[a];
    const to = corners[b];
    positions.push(from.x, from.y, from.z, to.x, to.y, to.z);
  }
}

function lineMaterial(color: number): THREE.LineBasicMaterial {
  return new THREE.LineBasicMaterial({
    color,
    toneMapped: false,
    depthTest: true,
    depthWrite: true,
  });
}

export class CollisionDebugDraw {
  readonly scene = new THREE.Scene();
  private readonly walkable: THREE.LineSegments;
  private readonly solid: THREE.LineSegments;
  private builtRevision = -1;

  constructor() {
    this.scene.background = new THREE.Color(CLEAR_COLOR);
    this.walkable = new THREE.LineSegments(new THREE.BufferGeometry(), lineMaterial(WALKABLE_COLOR));
    this.solid = new THREE.LineSegments(new THREE.BufferGeometry(), lineMaterial(SOLID_COLOR));
    this.walkable.frustumCulled = false;
    this.solid.frustumCulled = false;
    this.scene.add(this.walkable);
    this.scene.add(this.solid);
  }

  sync(world: CollisionWorld): void {
    if (this.builtRevision === world.revision) return;
    this.builtRevision = world.revision;
    this.rebuild(world);
  }

  private rebuild(world: CollisionWorld): void {
    const walkable: number[] = [];
    const solid: number[] = [];
    const corners: Vec3[] = new Array(8);
    world.forEach((_id, colliders) => {
      for (const collider of colliders) {
        const dest = collider.walkable === true ? walkable : solid;
        if (collider.kind === 'prism') {
          pushPrismEdges(collider.prism, dest);
          continue;
        }
        fillColliderCorners(collider, corners);
        pushBoxEdges(corners, dest);
      }
    });
    replaceLineGeometry(this.walkable, walkable);
    replaceLineGeometry(this.solid, solid);
  }
}

function fillColliderCorners(collider: Collider, out: Vec3[]): void {
  if (collider.kind === 'aabb') aabbCorners(collider.box, out);
  else if (collider.kind === 'obb') obbCorners(collider.obb, out);
}

function pushPrismEdges(prism: TrianglePrism, positions: number[]): void {
  const verts = prismVertices(prism);
  for (const [a, b] of PRISM_EDGE_PAIRS) {
    const from = verts[a];
    const to = verts[b];
    positions.push(from.x, from.y, from.z, to.x, to.y, to.z);
  }
}

function replaceLineGeometry(lines: THREE.LineSegments, positions: number[]): void {
  lines.geometry.dispose();
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  lines.geometry = geometry;
  lines.visible = positions.length > 0;
}
